/**
 * Network Bridge
 * 
 * Provides network connectivity information.
 * Full support via Navigator.onLine and NetworkInformation API.
 */

import { BaseBridge } from '../BaseBridge'
import type { BridgeAvailability, BridgeCapabilities, NetworkStatus } from '../types'

// NetworkInformation API types
interface NetworkInformation extends EventTarget {
  type?: 'bluetooth' | 'cellular' | 'ethernet' | 'none' | 'wifi' | 'wimax' | 'other' | 'unknown'
  effectiveType?: 'slow-2g' | '2g' | '3g' | '4g'
  downlink?: number
  rtt?: number
  saveData?: boolean
}

declare global {
  interface Navigator {
    connection?: NetworkInformation
    mozConnection?: NetworkInformation
    webkitConnection?: NetworkInformation
  }
}

export class NetworkBridge extends BaseBridge {
  readonly bridgeName = 'Network'
  readonly availability: BridgeAvailability = 'full'
  readonly methods = ['getNetworkStatus']
  
  private getConnection(): NetworkInformation | undefined {
    return navigator.connection || 
           navigator.mozConnection || 
           navigator.webkitConnection
  }
  
  async handleCall(method: string, _args: any[]): Promise<any> {
    switch (method) {
      case 'getNetworkStatus':
        return JSON.stringify(this.getNetworkStatus())
      default:
        throw this.createMethodNotFoundError(method)
    }
  }
  
  private getNetworkStatus(): NetworkStatus {
    const connection = this.getConnection()
    
    const status: NetworkStatus = {
      isConnected: navigator.onLine,
      type: this.mapConnectionType(connection?.type)
    }
    
    // Add detailed info if available (Chrome only)
    if (connection) {
      if (connection.effectiveType) {
        status.effectiveType = connection.effectiveType
      }
      if (typeof connection.downlink === 'number') {
        status.downlink = connection.downlink
      }
      if (typeof connection.rtt === 'number') {
        status.rtt = connection.rtt
      }
      if (typeof connection.saveData === 'boolean') {
        status.saveData = connection.saveData
      }
    }
    
    return status
  }
  
  private mapConnectionType(type?: string): NetworkStatus['type'] {
    if (!type) return navigator.onLine ? 'unknown' : 'none'
    
    switch (type) {
      case 'wifi':
        return 'wifi'
      case 'cellular':
        return 'cellular'
      case 'ethernet':
        return 'ethernet'
      case 'none':
        return 'none'
      default:
        return 'unknown'
    }
  }
  
  getCapabilities(): BridgeCapabilities {
    const hasDetailedInfo = !!this.getConnection()
    
    return {
      getNetworkStatus: { 
        available: true,
        fallback: hasDetailedInfo ? undefined : 'Basic connectivity only (effectiveType not available)'
      }
    }
  }
}

